import styled from 'styled-components';

// ========================================
// Resources Grid
// ========================================

export const ResourcesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  justify-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1rem;
  }
  
  @media (max-width: 480px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`;

// ========================================
// Downloadable Resource Cards
// ========================================

export const DownloadableResourceCard = styled.div`
  background: white;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  border: 2px solid transparent;
  display: flex;
  flex-direction: column;
  max-width: 500px;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    border-color: rgba(217, 125, 43, 0.3);
  }

  @media (max-width: 768px) {
    max-width: 300px;
  }
  
  @media (max-width: 480px) {
    flex-direction: row;
    max-width: 100%;
  }
`;

export const ResourcePreviewImage = styled.div`
  width: 100%;
  height: 200px;
  overflow: hidden;
  background: linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%);
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
  }
  
  ${DownloadableResourceCard}:hover & img {
    transform: scale(1.05);
  }
  
  @media (max-width: 768px) {
    height: 150px;
  }
  
  @media (max-width: 480px) {
    width: 135px;
    min-width: 100px;
    height: 100%;
    align-self: stretch;
  }
`;

export const ResourceContent = styled.div`
  padding: 1.5rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    padding: 0.75rem;
  }
  
  @media (max-width: 480px) {
    padding: 0.5rem 0.65rem 0.5rem 0.65rem;
    justify-content: center;
  }
`;

export const ResourceIconBadge = styled.div`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
  z-index: 10;
  
  svg {
    color: #D97D2B;
    font-size: 2rem;
  }
  
  @media (max-width: 768px) {
    width: 30px;
    height: 30px;
    top: 0.5rem;
    right: 0.5rem;
    
    svg {
      font-size: 1.1rem;
    }
  }
  
  @media (max-width: 480px) {
    width: 26px;
    height: 26px;
    top: 0.5rem;
    right: 0.5rem;
    
    svg {
      font-size: 0.9rem;
    }
  }
`;

export const DownloadButton = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.8rem 2rem;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  text-decoration: none;
  margin-top: auto;
  
  &:hover {
    background: linear-gradient(135deg, #C16D1F 0%, #E09538 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(217, 125, 43, 0.3);
  }
  
  svg {
    font-size: 1.1rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.5rem 1rem;
    font-size: 0.8rem;
    
    svg {
      font-size: 0.9rem;
    }
  }
  
  @media (max-width: 480px) {
    padding: 0.35rem 0.7rem;
    font-size: 0.7rem;
    gap: 0.25rem;
    margin-top: 0.3rem;
    
    svg {
      font-size: 0.8rem;
    }
  }
`;

// ========================================
// Placeholder Resource Cards
// ========================================

export const ResourceCard = styled.div`
  background: white;
  border-radius: 20px;
  padding: 2.5rem;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  text-align: center;
  border: 2px solid transparent;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.12);
    border-color: rgba(217, 125, 43, 0.3);
  }
`;

export const ResourceIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1.5rem;
  font-size: 2.5rem;
  color: white;
  transition: all 0.3s ease;
`;

export const ResourceTitle = styled.h3`
  font-size: 1.4rem;
  color: #1E4C5C;
  margin-bottom: 1rem;
  font-weight: 600;
  
  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 0.35rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.95rem;
    margin-bottom: 0.25rem;
  }
`;

export const ResourceDescription = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
    line-height: 1.35;
    margin-bottom: 0.5rem;
  }
  
  @media (max-width: 480px) {
    font-size: 0.75rem;
    line-height: 1.3;
    margin-bottom: 0.35rem;
    display: -webkit-box;
    -webkit-line-clamp: 3;
    -webkit-box-orient: vertical;
    overflow: hidden;
  }
`;

export const ResourceButton = styled.button`
  padding: 0.8rem 2rem;
  background: linear-gradient(135deg, #D97D2B 0%, #F5A64A 100%);
  color: white;
  border: none;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #C16D1F 0%, #E09538 100%);
    transform: translateY(-2px);
    box-shadow: 0 4px 15px rgba(217, 125, 43, 0.3);
  }
  
  &:disabled {
    background: #cccccc;
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

export const ComingSoonBadge = styled.span`
  display: inline-block;
  background: #FCD517;
  color: #1E4C5C;
  padding: 0.4rem 1rem;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-top: 0.5rem;
`;

